import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Stack,
  useMediaQuery
} from '@mui/material';
import { Visibility, VisibilityOff, LightMode, DarkMode, Person } from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import { useTheme as useCustomTheme } from '../../ThemeContext';
import { supabase } from '../../supabaseClient';
import { useTheme as useMuiTheme } from '@mui/material/styles';

const UserSettings = () => {
  const { user } = useContext(AuthContext);
  const muiTheme = useMuiTheme();
  const { mode, setMode } = useCustomTheme();
  const navigate = useNavigate();
  const isMobile = muiTheme.breakpoints ? muiTheme.breakpoints.down('sm') : false;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    fullName: '',
    email: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);

  useEffect(() => {
    let ignore = false;
    const fetchUserDataWrapper = async () => {
      try {
        setLoading(true);
        setError(null);
        if (!user?.id) throw new Error('Geen gebruiker ID gevonden');
        let profileData;
        const { data, error } = await supabase
          .from('profiles')
          .select('full_name, email, theme_preference')
          .eq('id', user.id)
          .single();
        if (error) throw error;
        if (!data) {
          const { data: newProfile, error: createError } = await supabase
            .from('profiles')
            .insert({
              id: user.id,
              email: user.email,
              username: user.email?.split('@')[0] || '',
              full_name: '',
              theme_preference: 'light',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select()
            .single();
          if (createError) throw createError;
          profileData = newProfile;
        } else {
          profileData = data;
        }
        if (profileData.theme_preference) {
          setMode(profileData.theme_preference);
        }
        setFormData({
          username: data.username || '',
          fullName: data.full_name || '',
          email: data.email || user.email || ''
        });
      } catch (error) {
        setError(`Fout bij het ophalen van gebruikersgegevens: ${error.message}`);
        setFormData({
          username: user?.email?.split('@')[0] || '',
          fullName: '',
          email: user?.email || ''
        });
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchUserDataWrapper();
    return () => { ignore = true; };
  }, [user, setMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: formData.fullName,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      if (error) throw error;
      setSuccess('Gebruikersgegevens zijn succesvol bijgewerkt');
    } catch (error) {
      setError(`Fout bij het bijwerken van gebruikersgegevens: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setPasswordError(null);
      setPasswordSuccess(null);
      if (!passwordData.currentPassword) throw new Error('Vul je huidige wachtwoord in');
      if (!passwordData.newPassword) throw new Error('Vul een nieuw wachtwoord in');
      if (passwordData.newPassword !== passwordData.confirmPassword) throw new Error('Nieuwe wachtwoorden komen niet overeen');
      if (passwordData.newPassword.length < 6) throw new Error('Wachtwoord moet minimaal 6 karakters lang zijn');
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordData.currentPassword,
      });
      if (signInError) {
        if (signInError.message.includes('Invalid login credentials')) throw new Error('Het huidige wachtwoord is onjuist');
        throw new Error(`Inloggen mislukt: ${signInError.message}`);
      }
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });
      if (updateError) throw new Error(`Wachtwoord wijzigen mislukt: ${updateError.message}`);
      setPasswordSuccess('Je wachtwoord is succesvol gewijzigd! Je wordt nu uitgelogd...');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/login', { state: { message: 'Wachtwoord succesvol gewijzigd. Log opnieuw in met je nieuwe wachtwoord.' } });
      }, 2000);
    } catch (error) {
      setPasswordError(error.message);
      setTimeout(() => { setPasswordError(null); }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };
  
  const handleNameChange = (e) => {
    setFormData(prev => ({ ...prev, fullName: e.target.value }));
  };

  const handleThemeChange = async (event, newMode) => {
    if (newMode !== null) {
      try {
        setLoading(true);
        const { error } = await supabase
          .from('profiles')
          .update({
            theme_preference: newMode,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
        if (error) throw error;
        setMode(newMode);
      } catch (error) {
        setError(`Fout bij het opslaan van thema voorkeur: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading && !formData.email) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // --- RENDER ---
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Paper elevation={0} sx={{
        background: `linear-gradient(135deg, ${muiTheme.palette.primary.main} 0%, ${muiTheme.palette.primary.dark} 100%)`,
        color: 'primary.contrastText',
        py: 4,
        px: { xs: 2, md: 4 },
        mb: 2,
        borderRadius: 0,
        textAlign: 'center',
        width: '100vw',
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
        minWidth: '100vw',
        maxWidth: '100vw',
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Gebruikersinstellingen
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Beheer je profiel, thema en wachtwoord
        </Typography>
      </Paper>

      <Box sx={{ maxWidth: 420, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Profiel Card */}
        <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
          <Stack direction="column" alignItems="center" spacing={2}>
            <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: 32 }}>
              {formData.fullName?.[0]?.toUpperCase() || <Person fontSize="large" />}
            </Avatar>
            <Typography variant="h6">Profiel</Typography>
            {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ width: '100%' }}>{success}</Alert>}
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <TextField
            fullWidth
            label="Volledige naam"
            name="full_name"
            value={formData.fullName || ''}
            onChange={handleNameChange}
            margin="normal"
                inputProps={{ autoComplete: "off", spellCheck: "false" }}
          />
          <TextField
            fullWidth
            label="Email"
            value={formData.email}
            disabled
            margin="normal"
          />
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </Box>
        </form>
          </Stack>
        </Paper>

        {/* Thema Card */}
        <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
          <Stack direction="column" alignItems="center" spacing={2}>
            <Typography variant="h6">Thema</Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleThemeChange}
          aria-label="thema selectie"
              sx={{ width: '100%' }}
        >
              <ToggleButton value="light" aria-label="licht thema" sx={{ flex: 1 }}>
                <LightMode sx={{ mr: 1 }} /> Licht
          </ToggleButton>
              <ToggleButton value="dark" aria-label="donker thema" sx={{ flex: 1 }}>
                <DarkMode sx={{ mr: 1 }} /> Donker
          </ToggleButton>
        </ToggleButtonGroup>
          </Stack>
        </Paper>

        {/* Wachtwoord Card */}
        <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
          <Stack direction="column" alignItems="center" spacing={2}>
            <Typography variant="h6">Wachtwoord wijzigen</Typography>
            {passwordError && <Alert severity="error" sx={{ width: '100%' }}>{passwordError}</Alert>}
            {passwordSuccess && <Alert severity="success" sx={{ width: '100%' }}>{passwordSuccess}</Alert>}
            <form onSubmit={handlePasswordChange} style={{ width: '100%' }}>
          <TextField
                name="email"
                type="email"
                autoComplete="username"
                value={user?.email || ''}
                sx={{ position: 'absolute', left: '-9999px' }}
                tabIndex={-1}
          />
          <TextField
            fullWidth
            label="Huidig wachtwoord"
            type={showPasswords.current ? 'text' : 'password'}
            name="currentPassword"
            value={passwordData.currentPassword}
                onChange={e => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
            required
            margin="normal"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle current password visibility"
                    onClick={() => togglePasswordVisibility('current')}
                    edge="end"
                  >
                    {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            fullWidth
            label="Nieuw wachtwoord"
            type={showPasswords.new ? 'text' : 'password'}
            name="newPassword"
            value={passwordData.newPassword}
                onChange={e => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
            required
            margin="normal"
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle new password visibility"
                    onClick={() => togglePasswordVisibility('new')}
                    edge="end"
                  >
                    {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            fullWidth
            label="Bevestig nieuw wachtwoord"
            type={showPasswords.confirm ? 'text' : 'password'}
            name="confirmPassword"
            value={passwordData.confirmPassword}
                onChange={e => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
            required
            margin="normal"
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => togglePasswordVisibility('confirm')}
                    edge="end"
                  >
                    {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
              <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
            >
              {loading ? 'Wachtwoord Wijzigen...' : 'Wachtwoord Wijzigen'}
            </Button>
          </Box>
        </form>
          </Stack>
        </Paper>
        <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 2, fontSize: 13 }}>
          Account ID: {user?.id}
        </Box>
      </Box>
    </Box>
  );
};

export default UserSettings;
